"use client";

import React, { useCallback, memo } from "react";
import { CloseButton, Dialog, Portal, Box } from "@chakra-ui/react";

type OpenChangeDetails = {
  open: boolean;
};

export type WithGenericDialogProps = {
  title?: string;
  isOpen: boolean;
  onOpenChangeAction: (isOpen: boolean) => void;
  onCloseAction?: () => void;
  minContentHeight?: string;
  children: React.ReactNode;
};

function WithGenericDialog({
  isOpen,
  onOpenChangeAction,
  title = "Dialog",
  onCloseAction,
  minContentHeight,
  children,
}: WithGenericDialogProps) {
  const handleClose = useCallback(() => {
    onCloseAction?.();
    onOpenChangeAction(false);
  }, [onCloseAction, onOpenChangeAction]);

  const handleOpenChange = useCallback(
    (details: OpenChangeDetails) => {
      onOpenChangeAction(details.open);
    },
    [onOpenChangeAction],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={handleOpenChange}
      onEscapeKeyDown={handleClose}
      onInteractOutside={handleClose}
      lazyMount={true}
      unmountOnExit={true}
      immediate={true}
      // Dialog tasks should not auto-open the mobile keyboard unless a
      // specific search/compose surface opts into editable initial focus.
      restoreFocus={false}
      size="lg"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            bg="moveon.surface.bg"
            color="moveon.surface.fg"
            borderColor="moveon.surface.border"
            minH={minContentHeight}
            maxH="90vh"
            overflowY="auto"
          >
            <Dialog.Header>
              <Box
                maxWidth="full"
                overflow="hidden"
                wordBreak="break-word"
                whiteSpace="normal"
              >
                <Dialog.Title>{title}</Dialog.Title>
              </Box>
              <Dialog.CloseTrigger asChild onClick={handleClose}>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body>{children}</Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

export default memo(WithGenericDialog);
